import { useEffect, useRef, useState, useContext } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Instagram, ExternalLink, Play, Filter, Star } from "lucide-react";
import axios from "axios";
import { SiteContext } from "@/App";

function PostCard({ post, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const hasImage = post.media_url || post.thumbnail_url;
  const imageUrl = post.media_url || post.thumbnail_url;
  const isVideo = post.media_type === "VIDEO";

  return (
    <motion.a
      ref={ref}
      href={post.post_url || "https://www.instagram.com/ludbradleytattoo/"}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: Math.min(index * 0.08, 0.5), duration: 0.5 }}
      data-testid={`post-card-${index}`}
      className="card-hover block bg-ink-900 border border-ink-600 rounded-sm overflow-hidden group cursor-pointer"
    >
      <div className="relative aspect-square bg-ink-800 overflow-hidden">
        {hasImage ? (
          <>
            <img
              src={imageUrl}
              alt={post.title || "Lud Bradley tattoo work"}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
            {isVideo && (
              <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm rounded-full p-1.5">
                <Play size={12} className="text-white fill-white" />
              </div>
            )}
            {post.starred && (
              <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm rounded-full p-1.5">
                <Star size={12} className="text-amber-400 fill-amber-400" />
              </div>
            )}
            {post.category && post.category !== "Work" && (
              <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm rounded-sm px-2 py-1">
                <span className="font-manrope text-[8px] tracking-widest uppercase text-verde">{post.category}</span>
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Instagram size={32} className="text-ink-600" />
          </div>
        )}
      </div>

      {post.title && (
        <div className="p-4">
          <p className="font-manrope text-[#A3A3A3] text-xs leading-relaxed line-clamp-2 group-hover:text-[#E5E5E5] transition-colors duration-300">
            {post.title}
          </p>
          <div className="mt-3 pt-3 border-t border-ink-600 flex items-center gap-2">
            <Instagram size={10} className="text-verde" />
            <span className="font-manrope text-[9px] tracking-widest uppercase text-verde">
              View on Instagram
            </span>
          </div>
        </div>
      )}
    </motion.a>
  );
}

function GalleryHeadliner({ images }) {
  if (!images || images.length === 0) return null;

  return (
    <div data-testid="gallery-headliner" className="mb-12">
      <p className="font-manrope text-verde text-[10px] tracking-[0.3em] uppercase mb-4 flex items-center gap-2">
        <Star size={12} className="text-amber-400 fill-amber-400" />
        Trending Work
      </p>
      <div className={`grid gap-3 md:gap-4 ${
        images.length === 1 ? "grid-cols-1 max-w-lg" :
        images.length === 2 ? "grid-cols-2" :
        images.length <= 4 ? "grid-cols-2 md:grid-cols-4" :
        "grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
      }`}>
        {images.map((img, i) => (
          <motion.div
            key={img.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.1, duration: 0.5 }}
            className="relative aspect-square bg-ink-800 rounded-sm overflow-hidden border border-verde/20 group"
          >
            <img
              src={img.url}
              alt={img.caption || "Featured tattoo work"}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            {img.caption && (
              <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <p className="font-manrope text-[10px] text-white">{img.caption}</p>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default function InstagramFeed() {
  const { settings, API } = useContext(SiteContext);
  const [posts, setPosts] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null); // null = not loaded yet
  const [showAll, setShowAll] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  const handle = settings?.instagram_handle || "ludbradleytattoo";

  useEffect(() => {
    axios.get(`${API}/posts`).then(r => setPosts(r.data)).catch(() => {});
    axios.get(`${API}/gallery`).then(r => setGalleryImages(r.data || [])).catch(() => {});
    axios.get(`${API}/posts/categories`).then(r => {
      const cats = r.data || [];
      setCategories(cats);
      // Default to "Featured" if there are starred posts, otherwise "All"
      const hasFeatured = cats.some(c => c.category === "Featured");
      setActiveCategory(hasFeatured ? "Featured" : "All");
    }).catch(() => setActiveCategory("All"));
  }, [API]);

  const filteredPosts = activeCategory === "All"
    ? posts
    : activeCategory === "Featured"
      ? posts.filter(p => p.starred)
      : posts.filter(p => p.category === activeCategory);

  const displayPosts = showAll ? filteredPosts : filteredPosts.slice(0, 12);
  const hasMultipleCategories = categories.length > 1 || categories.some(c => c.category === "Featured");

  if (activeCategory === null) return null; // Loading

  return (
    <section data-testid="instagram-section" ref={ref} className="py-24 md:py-32 bg-ink-950">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12"
        >
          <div>
            <p className="font-manrope text-verde text-xs tracking-[0.3em] uppercase mb-4">Latest Work</p>
            <div className="section-divider mb-8" />
            <h2 className="font-cinzel text-4xl md:text-5xl font-bold text-[#E5E5E5] leading-tight">
              Gallery &amp;<br />Highlights
            </h2>
          </div>
          <a
            data-testid="instagram-follow-link"
            href={`https://www.instagram.com/${handle}/`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 btn-ghost self-start md:self-end"
          >
            <Instagram size={14} />
            @{handle}
            <ExternalLink size={12} />
          </a>
        </motion.div>

        {/* Gallery Headliner */}
        <GalleryHeadliner images={galleryImages} />

        {/* Category Filters */}
        {hasMultipleCategories && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.5 }}
            data-testid="gallery-filters"
            className="flex items-center gap-2 mb-10 overflow-x-auto pb-2 scrollbar-hide"
          >
            <Filter size={12} className="text-[#525252] flex-shrink-0 mr-1" />
            <button
              data-testid="filter-all"
              onClick={() => { setActiveCategory("All"); setShowAll(false); }}
              className={`font-manrope text-[10px] tracking-widest uppercase px-4 py-2 rounded-sm border transition-colors flex-shrink-0 ${
                activeCategory === "All"
                  ? "bg-verde/20 border-verde text-verde"
                  : "border-ink-600 text-[#525252] hover:text-[#A3A3A3] hover:border-ink-500"
              }`}
            >
              All ({posts.length})
            </button>
            {categories.map(cat => (
              <button
                key={cat.category}
                data-testid={`filter-${cat.category.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => { setActiveCategory(cat.category); setShowAll(false); }}
                className={`font-manrope text-[10px] tracking-widest uppercase px-4 py-2 rounded-sm border transition-colors flex-shrink-0 ${
                  activeCategory === cat.category
                    ? cat.category === "Featured"
                      ? "bg-amber-400/20 border-amber-400 text-amber-400"
                      : "bg-verde/20 border-verde text-verde"
                    : "border-ink-600 text-[#525252] hover:text-[#A3A3A3] hover:border-ink-500"
                }`}
              >
                {cat.category === "Featured" && <Star size={10} className="inline mr-1 fill-current" />}
                {cat.category} ({cat.count})
              </button>
            ))}
          </motion.div>
        )}

        {/* Post Grid */}
        {displayPosts.length > 0 ? (
          <>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeCategory}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4"
              >
                {displayPosts.map((post, i) => (
                  <PostCard key={post.id || i} post={post} index={i} />
                ))}
              </motion.div>
            </AnimatePresence>

            {filteredPosts.length > 12 && (
              <div className="mt-10 text-center">
                <button
                  data-testid="show-more-posts"
                  onClick={() => setShowAll(!showAll)}
                  className="btn-ghost"
                >
                  {showAll ? `Show Less` : `Show All ${filteredPosts.length} Posts`}
                </button>
              </div>
            )}
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="border border-ink-600 rounded-sm p-12 md:p-16 text-center"
          >
            <Instagram size={40} className="text-verde/40 mx-auto mb-6" />
            <h3 className="font-cinzel text-xl md:text-2xl font-bold text-[#E5E5E5] mb-3">
              {activeCategory !== "All" ? `No ${activeCategory} posts yet` : "Follow the Journey"}
            </h3>
            <p className="font-manrope text-[#525252] text-sm max-w-md mx-auto mb-8 leading-relaxed">
              {activeCategory === "Featured"
                ? "Star posts in the admin panel to feature them here."
                : activeCategory !== "All"
                  ? "Try selecting a different category or view all posts."
                  : "Explore Lud's latest black and grey realism work on Instagram."}
            </p>
            <button onClick={() => setActiveCategory("All")} className="btn-verde">View All Posts</button>
          </motion.div>
        )}

        {/* Follow CTA */}
        {displayPosts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="mt-16 text-center"
          >
            <p className="font-manrope text-[#525252] text-sm mb-6">
              Follow along for daily work, behind-the-scenes, and convention updates.
            </p>
            <a
              data-testid="instagram-follow-cta"
              href={`https://www.instagram.com/${handle}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 btn-verde"
            >
              <Instagram size={14} />
              Follow @{handle}
            </a>
          </motion.div>
        )}
      </div>
    </section>
  );
}
